/**
 * Validate environment variables at startup (development only).
 *
 * Client-side VITE_ vars are checked directly. Server-side vars
 * (OPENAI_API_KEY, PLANTID_API_KEY, MESHY_API_KEY, PIMLICO_API_KEY)
 * are validated in their respective API route modules under packages/api/src/clients/.
 */
function validateEnvVars(): void {
  if (!import.meta.env.DEV) return

  const requiredClientVars: Array<{
    name: string
    value: string | undefined
    description: string
  }> = [
    {
      name: 'VITE_APPKIT_PROJECT_ID',
      value: import.meta.env.VITE_APPKIT_PROJECT_ID,
      description: 'Reown AppKit / WalletConnect',
    },
    {
      name: 'VITE_PASSKEY_RP_ID',
      value: import.meta.env.VITE_PASSKEY_RP_ID,
      description: 'passkey relying party ID',
    },
  ]

  const missingClient = requiredClientVars.filter((v) => !v.value)
  if (missingClient.length > 0) {
    console.warn(
      `[WEFA] Missing client env vars (some features will be unavailable):\n${missingClient.map((v) => `  - ${v.name}: ${v.description}`).join('\n')}\n  Add them to .env.local — see CLAUDE.md for details.`,
    )
  }

  // Server-side vars (OPENAI_API_KEY, PLANTID_API_KEY, MESHY_API_KEY, PIMLICO_API_KEY)
  // are validated in their respective API route modules under packages/api/src/clients/.
}

validateEnvVars()

function asBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback
  return value === 'true'
}

function asNumber(value: string | undefined, fallback: number): number {
  if (!value) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function asStringList(value: string | undefined): string[] {
  if (!value) return []
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
}

function asPositiveNumber(value: string | undefined, fallback: number): number {
  if (!value) return fallback
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return parsed
}

type YjsTransport = 'webrtc' | 'websocket' | 'hybrid'

function asYjsTransport(value: string | undefined, websocketUrls: string[]): YjsTransport {
  if (value === 'webrtc' || value === 'websocket' || value === 'hybrid') {
    if (value !== 'webrtc' && websocketUrls.length === 0) return 'webrtc'
    return value
  }
  return websocketUrls.length > 0 ? 'hybrid' : 'webrtc'
}

const signalingUrls = asStringList(import.meta.env.VITE_SIGNALING_URLS)
const websocketUrls = asStringList(import.meta.env.VITE_YJS_WEBSOCKET_URLS)

export const config = {
  version: '1.0.0',
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  appKitProjectId:
    import.meta.env.VITE_APPKIT_PROJECT_ID ?? import.meta.env.VITE_WALLETCONNECT_PROJECT_ID ?? '',
  chainId: asNumber(import.meta.env.VITE_CHAIN_ID, 84532),
  sentryDsn: import.meta.env.VITE_SENTRY_DSN ?? '',
  signalingUrls,
  yjs: {
    transport: asYjsTransport(import.meta.env.VITE_YJS_TRANSPORT, websocketUrls),
    signalingUrls,
    websocketUrls,
  },
  passkey: {
    rpId: import.meta.env.VITE_PASSKEY_RP_ID ?? '',
    rpName: import.meta.env.VITE_PASSKEY_RP_NAME ?? 'WEFA',
    usernamePrefix: import.meta.env.VITE_PASSKEY_USERNAME_PREFIX ?? 'keeper',
  },
  pimlico: {
    // API key is now server-side only (proxied via /api/auth/pimlico)
    sponsorshipPolicyId: import.meta.env.VITE_PIMLICO_SPONSORSHIP_POLICY_ID ?? '',
  },
  eas: {
    contractAddress: '0x4200000000000000000000000000000000000021',
    schemaRegistryAddress: '0x4200000000000000000000000000000000000020',
  },
  atproto: {
    appName: import.meta.env.VITE_ATPROTO_APP_NAME ?? 'WEFA',
    service: import.meta.env.VITE_ATPROTO_SERVICE ?? 'https://bsky.social',
  },
  unlock: {
    lockAddress: import.meta.env.VITE_UNLOCK_LOCK_ADDRESS ?? '',
    checkoutUrl: import.meta.env.VITE_UNLOCK_CHECKOUT_URL ?? '',
    cacheTtlMs: asPositiveNumber(import.meta.env.VITE_UNLOCK_CACHE_TTL_MS, 10 * 60 * 1000),
  },
  autonomousWorld: {
    registryAddress: import.meta.env.VITE_AUTONOMOUS_WORLD_REGISTRY_ADDRESS ?? '',
    chainId: asNumber(
      import.meta.env.VITE_AUTONOMOUS_WORLD_CHAIN_ID,
      asNumber(import.meta.env.VITE_CHAIN_ID, 84532),
    ),
    rpcUrl: import.meta.env.VITE_AUTONOMOUS_WORLD_RPC_URL ?? '',
  },
  greenGoods: {
    endpoint: import.meta.env.VITE_GREEN_GOODS_ENDPOINT ?? '',
    enabled: asBoolean(import.meta.env.VITE_GREEN_GOODS_ENABLED, false),
  },
  logLevel: (import.meta.env.VITE_LOG_LEVEL ?? (import.meta.env.DEV ? 'debug' : 'warn')) as
    | 'debug'
    | 'info'
    | 'warn'
    | 'error',
  qa: {
    validationEnabled: asBoolean(import.meta.env.VITE_QA_VALIDATION, import.meta.env.DEV),
  },
  contracts: {
    keeperAddress: import.meta.env.VITE_KEEPER_CONTRACT_ADDRESS ?? '',
    plantAddress: import.meta.env.VITE_PLANT_CONTRACT_ADDRESS ?? '',
    creatureAddress: import.meta.env.VITE_CREATURE_CONTRACT_ADDRESS ?? '',
    accountFactoryAddress: import.meta.env.VITE_ACCOUNT_FACTORY_ADDRESS ?? '',
    resolverAddress: import.meta.env.VITE_RESOLVER_CONTRACT_ADDRESS ?? '',
    tbaRegistryAddress: '0x000000006551c19487814612e58FE06813775758',
    tbaImplementationAddress: import.meta.env.VITE_TBA_IMPLEMENTATION_ADDRESS ?? '',
  },
  features: {
    kidFirstShell: asBoolean(import.meta.env.VITE_FEATURE_KID_FIRST_SHELL, import.meta.env.DEV),
    ar: asBoolean(import.meta.env.VITE_FEATURE_AR, true),
    meshyGeneration: asBoolean(import.meta.env.VITE_FEATURE_MESHY, true),
    onchainAttestations: asBoolean(import.meta.env.VITE_FEATURE_ONCHAIN_ATTESTATIONS, false),
    onchainNfts: asBoolean(import.meta.env.VITE_FEATURE_ONCHAIN_NFTS, false),
  },
} as const
